import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { MediaV2 } from '../../../../../../../shared/frontend/components/presentational/media';
import HealthOverAttemptWidget from '../../../streamWidget/containers/healthOverAttempt';
import VersusTimelineWidget from '../../../streamWidget/containers/versusTimelineWidget';
import VersusWidget from '../../../streamWidget/containers/versusWidget';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { InteractiveStreamPreview } from './shared';

function getDimensionsForNumStreams(num: number) {
  const BASE_WIDTH = 450;
  const BASE_HEIGHT = 253;

  if (num > 2) {
    return {
      width: Math.floor((BASE_WIDTH * 2) / num),
      height: Math.floor((BASE_HEIGHT * 2) / num)
    };
  }

  return {
    width: BASE_WIDTH,
    height: BASE_HEIGHT
  };
}

type TGuildShowdownProps = {
  bossSlug: string;
  className?: string;
  graphGuildQueries: string;
  guilds: any[];
  numVisibleStreams: number;
  raidSlug: string;
  showdownTitle: string;
  showGraph: boolean;
  showTimeline: boolean;
  showVersus: boolean;
  timelineWidgetQuery: string;
};

export function GuildShowdown(props: TGuildShowdownProps) {
  const numVisibleStreams = props.numVisibleStreams;
  const { width, height } = getDimensionsForNumStreams(numVisibleStreams);
  const [orderedGuildIds, setOrderedGuildIds] = useState<string[]>([]);

  const allGuilds: any[] = props.guilds.map((g: any) => ({
    width,
    height,
    thumbnailWidth: width,
    thumbnailHeight: height,
    live: false,
    autoplay: false,
    className: 'stream--larger',
    ...g
  }));

  const guildQs = allGuilds.map((g: any) => g.guildId).join(',');

  const guilds = useMemo(() => {
    const visibleGuilds = allGuilds.filter((g: any) => g.showStream);

    if (orderedGuildIds.length === 0) {
      return visibleGuilds;
    }

    const guildById = new Map(visibleGuilds.map((guild) => [String(guild.guildId), guild]));
    const orderedVisibleGuilds = orderedGuildIds.reduce<any[]>((orderedGuilds, guildId) => {
      const guild = guildById.get(guildId);
      if (guild) {
        orderedGuilds.push(guild);
      }

      return orderedGuilds;
    }, []);
    const orderedGuildIdSet = new Set(orderedGuildIds);
    const remainingGuilds = visibleGuilds.filter((guild) => !orderedGuildIdSet.has(String(guild.guildId)));

    return [...orderedVisibleGuilds, ...remainingGuilds];
  }, [allGuilds, orderedGuildIds]);

  const [isShowingStreams, setShowingStreams] = useState(false);

  const guildForceName = allGuilds.map((g) => `${g.guildId}:${g.name}`).join(',');

  const getStreamComponent = (guild: any, overrideLive?: boolean) => {
    return (
      <InteractiveStreamPreview
        type="twitch"
        thumbnailUrl={`https://static-cdn.jtvnw.net/previews-ttv/live_user_${guild.streamerName}-${guild.width}x${guild.height}.jpg`}
        width={guild.width}
        height={guild.height}
        thumbnailWidth={guild.width}
        thumbnailHeight={guild.height}
        viewerCount={0}
        streamIdentifier={guild.streamerName}
        isFeatured={true}
        makeLiveImmediately={true}
        featuredTitle={
          <span>
            <img src={guild.logoUrl} style={guild.logoStyle} /> {guild.name}
          </span>
        }
        live={overrideLive ?? guild.live}
        autoplay={guild.autoplay}
        className={guild.className}
        style={{
          maxWidth: '90%'
        }}
      />
    );
  };

  return (
    <div className={props.className}>
      <div
        className="slds-text-heading--x-large slds-text-align--center rio-text-shadow--normal widget-theme-dragonflight"
        style={{ fontSize: '3rem', color: '#ffbd0a' }}
      >
        {props.showdownTitle}
      </div>

      {props.showVersus && guilds.length > 0 && (
        <div
          style={{ minHeight: 225, width: '100%' }}
          className="slds-p-bottom--small slds-p-horizontal--small rio-p-horizontal-mobi--x-small"
        >
          <VersusWidget
            onGuildOrderChange={setOrderedGuildIds}
            queryOverride={`?raid=${props.raidSlug}&difficulty=mythic&guild_ids=${guildQs}&theme=dragonflight&chromargb=transparent&hide_logo=1&boss=${
              props.bossSlug
            }&refresh=30&wrap=1&force_name=${guildForceName}`}
          />
        </div>
      )}

      <div className="slds-grid slds-wrap slds-grid--align-center slds-m-top--large">
        {guilds.slice(0, numVisibleStreams).map((guild) => (
          <div
            key={guild.guildId}
            className={`slds-max-small-size--1-of-1 slds-size--1-of-${numVisibleStreams} slds-text-align--center slds-p-bottom--large`}
          >
            <MediaV2 greaterThanOrEqual="md">{getStreamComponent(guild)}</MediaV2>
            <MediaV2 lessThan="md">{getStreamComponent(guild, false)}</MediaV2>
          </div>
        ))}
      </div>

      {guilds.length > numVisibleStreams && (
        <>
          {isShowingStreams && (
            <div className="slds-grid slds-wrap slds-grid--align-center slds-m-top--large">
              {guilds.slice(numVisibleStreams).map((guild) => (
                <div
                  key={guild.guildId}
                  className={`slds-max-small-size--1-of-1 slds-size--1-of-${numVisibleStreams} slds-text-align--center slds-p-bottom--large`}
                >
                  <MediaV2 greaterThanOrEqual="md">{getStreamComponent(guild)}</MediaV2>
                  <MediaV2 lessThan="md">{getStreamComponent(guild, false)}</MediaV2>
                </div>
              ))}
            </div>
          )}

          <div className="slds-text-heading--medium slds-p-vertical--small slds-text-align--center rio-realm-link">
            <Link
              to=""
              className="text-bold"
              onClick={(ev: any) => {
                ev.stopPropagation();
                ev.preventDefault();
                setShowingStreams(!isShowingStreams);
              }}
            >
              {isShowingStreams ? 'HIDE STREAMS ' : 'SHOW ALL STREAMS'}
            </Link>
          </div>
        </>
      )}

      {props.showGraph && (
        <div className="slds-p-around--x-small">
          <HealthOverAttemptWidget
            showPullAnnotations={false}
            hideTitle={true}
            hideSubtitle={false}
            subtitle={t('mastheads:rwf.showdown-graph.subtitle', {
              bossName: t(`raids:${props.raidSlug}.bosses.${props.bossSlug}.name`)
            })}
            height={250}
            queryOverride={`?raid=${props.raidSlug}&difficulty=mythic&guilds=${encodeURIComponent(props.graphGuildQueries)}&boss=${
              props.bossSlug
            }&refresh=60&hideLogo=1`}
          />
        </div>
      )}

      {props.showTimeline && <VersusTimelineWidget queryOverride={props.timelineWidgetQuery} />}
    </div>
  );
}
